import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getProjects, getProjectFiles } from '../services/editorApi';

export interface EditorFile {
  id: string;
  name: string;
  path: string;
  content: string;
  language: string;
  isDirty: boolean;
  projectId: string;
  file_type?: string; // 'FILE' or 'FOLDER'
}

export interface EditorProject {
  id: string;
  name: string;
  description: string;
  language: string;
}

export interface EditorTab {
  fileId: string;
  name: string;
  path: string;
  language: string;
  isComponent?: boolean;
  componentType?: 'testing' | 'autonomous' | 'terminal';
}

interface EditorState {
  // Projects
  projects: EditorProject[];
  currentProject: EditorProject | null;
  
  // Files
  files: EditorFile[];
  
  // Tabs
  openTabs: EditorTab[];
  activeTabId: string | null;
  
  // UI State
  sidebarOpen: boolean;
  sidebarWidth: number;
  showTerminal: boolean;
  
  // Actions - Projects
  setProjects: (projects: EditorProject[]) => void;
  setCurrentProject: (project: EditorProject | null) => void;
  addProject: (project: EditorProject) => void;
  updateProject: (id: string, updates: Partial<EditorProject>) => void;
  removeProject: (id: string) => void;
  
  // Actions - Files
  setFiles: (files: EditorFile[]) => void;
  openFile: (file: EditorFile) => void;
  closeFile: (fileId: string) => void;
  updateFileContent: (fileId: string, content: string) => void;
  saveFile: (fileId: string) => void;
  
  // Actions - Tabs
  setActiveTab: (tabId: string | null) => void;
  closeTab: (tabId: string) => void;
  
  // Actions - Component Tabs
  openComponentTab: (componentType: 'testing' | 'autonomous' | 'terminal', name: string) => void;

  // Actions - UI
  setSidebarOpen: (open: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setShowTerminal: (show: boolean) => void;
}

export const useEditorStore = create<EditorState>()(
  persist(
    (set, get) => ({
      // Initial state
      projects: [],
      currentProject: null,
      files: [],
      openTabs: [],
      activeTabId: null,
  sidebarOpen: true,
  sidebarWidth: 260,
  showTerminal: false,
      
      // Load projects from backend - just load the list, don't auto-select
      loadProjects: async () => {
        try {
          const projects = await getProjects();
          set({ projects });
          // Note: Don't auto-select a project - let user choose from the list
        } catch (error) {
          console.error('Failed to load projects:', error);
        }
      },
      
      // Load project files
      loadProjectFiles: async (projectId: string) => {
        try {
          const files = await getProjectFiles(projectId);
          set({
            files: files.map(f => ({
              id: f.id,
              name: f.name,
              path: f.path,
              content: f.content || '',
              language: f.language || 'plaintext',
              isDirty: false,
              projectId: f.project_id,
              file_type: f.file_type,
            })),
          });
        } catch (error) {
          console.error('Failed to load project files:', error);
        }
      },
      
      // Projects actions
      setProjects: (projects) => set({ projects }),
      setCurrentProject: (project) => set({ currentProject: project }),
      addProject: (project) => {
        const projects = get().projects;
        set({ projects: [project, ...projects] });
      },
      updateProject: (id, updates) => {
        const projects = get().projects;
        set({
          projects: projects.map((p) => (p.id === id ? { ...p, ...updates } : p)),
        });
        if (get().currentProject?.id === id) {
          set({ currentProject: { ...get().currentProject!, ...updates } });
        }
      },
      removeProject: (id) => {
        const projects = get().projects.filter((p) => p.id !== id);
        set({ projects });
        if (get().currentProject?.id === id) {
          set({ currentProject: null, files: [], openTabs: [], activeTabId: null });
        }
      },
      
      // Files actions
      setFiles: (files) => set({ files }),
      clearFiles: () => set({ files: [], openTabs: [], activeTabId: null }),
      openFile: (file) => {
        const { openTabs, activeTabId } = get();
        
        // Check if file is already open
        if (!openTabs.find((tab) => tab.fileId === file.id)) {
          set({
            openTabs: [
              ...openTabs,
              {
                fileId: file.id,
                name: file.name,
                path: file.path,
                language: file.language,
              },
            ],
          });
        }
        
        set({ activeTabId: file.id });
        
        // Add file to files list if not present
        if (!get().files.find((f) => f.id === file.id)) {
          set({ files: [...get().files, file] });
        }
      },
      closeFile: (fileId) => {
        set({
          openTabs: get().openTabs.filter((tab) => tab.fileId !== fileId),
        });
        if (get().activeTabId === fileId) {
          const remainingTabs = get().openTabs.filter((tab) => tab.fileId !== fileId);
          set({ activeTabId: remainingTabs.length > 0 ? remainingTabs[remainingTabs.length - 1].fileId : null });
        }
      },
      updateFileContent: (fileId, content) => {
        const files = get().files;
        set({
          files: files.map((f) =>
            f.id === fileId ? { ...f, content, isDirty: true } : f
          ),
        });
      },
      saveFile: (fileId) => {
        const files = get().files;
        set({
          files: files.map((f) =>
            f.id === fileId ? { ...f, isDirty: false } : f
          ),
        });
      },
      
      // Tabs actions
      setActiveTab: (tabId) => set({ activeTabId: tabId }),
      closeTab: (tabId) => {
        set({
          openTabs: get().openTabs.filter((tab) => tab.fileId !== tabId),
        });
        if (get().activeTabId === tabId) {
          const remainingTabs = get().openTabs.filter((tab) => tab.fileId !== tabId);
          set({ activeTabId: remainingTabs.length > 0 ? remainingTabs[remainingTabs.length - 1].fileId : null });
        }
      },
      
      // Component tab actions
      openComponentTab: (componentType, name) => {
        const { openTabs } = get();
        
        // Check if component tab already exists
        const existingTab = openTabs.find(tab => tab.isComponent && tab.componentType === componentType);
        
        if (existingTab) {
          // Just activate existing tab
          set({ activeTabId: existingTab.fileId });
          return;
        }
        
        // Create new tab if not exists
        const tabId = `component-${componentType}-${Date.now()}`;
        
        set({
          openTabs: [
            ...openTabs,
            {
              fileId: tabId,
              name,
              path: `/${componentType}`,
              language: 'component',
              isComponent: true,
              componentType
            }
          ],
          activeTabId: tabId
        });
      },

      // UI actions
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setSidebarWidth: (width) => set({ sidebarWidth: width }),
      setShowTerminal: (show) => set({ showTerminal: show }),
    }),
    {
      name: 'code-editor-storage',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        sidebarWidth: state.sidebarWidth,
        // Don't persist currentProject - let it be loaded on mount
        // currentProject: state.currentProject,
      }),
    }
  )
);